import { inject, Injectable } from "@angular/core";
import { AutoStakingState, AutoStakingStateService } from "@modules/staking/domain/+state/auto-staking.state.service";
import { Observable, of, tap, throwError } from "rxjs";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { AutoStakingRemote } from "@modules/staking/domain/infrastructure/auto-staking.remote";
import { GetStakingUserJourneyQuery } from "../../../../api/graphql/staking/generated/auto-staking.user-journey.service.graphql-gen";
import { UpdateSettingActionInput } from "@gql/staking";
import { catchError, switchMap } from "rxjs/operators";
import { UpdateSettingsMutation } from "../../../../api/graphql/staking/generated/auto-staking.update-state.service.graphql-gen";

@Injectable({
	providedIn: "root"
})
export class AutoStakingFacade {
	private readonly autoStakingStateService = inject(AutoStakingStateService);
	private readonly launchDarklyService = inject(LaunchDarklyService);
	private readonly autoStakingRemote = inject(AutoStakingRemote);

	public getStakingUserJourney$(): Observable<GetStakingUserJourneyQuery | string> {
		return this.autoStakingRemote.getStakingUserJourney$();
	}

	private getAutoStakingStateActionInput(state: boolean): UpdateSettingActionInput {
		return {
			autoStaking: {
				enabled: state
			}
		};
	}

	public setAutoStakingState(state: boolean): Observable<UpdateSettingsMutation> {
		return this.autoStakingRemote.updateAutoStakingState$(this.getAutoStakingStateActionInput(state)).pipe(
			tap((res) => {
				const optedInState = res.settingsUpdate.autoStake;
				this.autoStakingStateService.updateState({ autoStakingSwitchOn: optedInState });
			}),
			catchError((err) => {
				console.error(err);
				return throwError(() => err);
			})
		);
	}

	private getState$(): Observable<AutoStakingState> {
		return this.autoStakingStateService.getState$();
	}

	public isAutoStakingEnabled$(): Observable<boolean> {
		return this.launchDarklyService.isActive$(LD_FLAG.AUTO_STAKING);
	}

	public getOrFetchAutoStakingState$(): Observable<AutoStakingState> {
		return this.isAutoStakingEnabled$().pipe(
			switchMap((isEnabled) => {
				if (!isEnabled) {
					return of({
						autoStakingSwitchOn: false,
						hasError: false
					});
				}
				// If state is already set return it
				if (this.autoStakingStateService.isOptedInStateLoaded() && !this.autoStakingStateService.hasError()) {
					return this.getState$();
				}
				return this.autoStakingRemote.fetchAutoStakingState$().pipe(
					tap((data) => {
						if (!data) {
							this.autoStakingStateService.updateState({ hasError: true });
							return;
						}
						const autoStakingOptedIn = data?.settings?.autoStake?.enabled;
						this.autoStakingStateService.updateState({ autoStakingSwitchOn: autoStakingOptedIn });
					}),
					switchMap(() => this.getState$())
				);
			})
		);
	}
}
